"use client";

/**
 * This file contains unified milestone hooks that work for both brands and retailers.
 *
 * The implementation automatically determines whether to use brand_milestone or
 * retailer_milestone fields based on the milestone type passed in.
 *
 * Usage:
 * - Use the useBrandMilestone or useRetailerMilestone hooks directly:
 *
 *   const milestone = useBrandMilestone(SOME_BRAND_MILESTONE);
 *   const milestone = useRetailerMilestone(SOME_RETAILER_MILESTONE);
 *
 * - If necessary, you can also use the unified useMilestone hook directly:
 *
 *   const milestone = useMilestone(true, SOME_BRAND_MILESTONE);
 *                                   ^ isBrand
 *
 * The hook will automatically handle the correct field based on the milestone type.
 */
import {
  useQuery as useMilestonesQuery,
  getQueryKey as getMilestonesQueryKey,
} from "@faire/web-api--source/endpoints/www/api/user/milestones/userToken/list/get-hooks";
import { useMutation as useMilestoneMutation } from "@faire/web-api--source/endpoints/www/api/user/milestones/userToken/post-hooks";
import { IGetUserMilestonesResponse } from "@faire/web-api--source/indigofair/data/IGetUserMilestonesResponse";
import { IUserMilestone } from "@faire/web-api--source/indigofair/data/IUserMilestone";
import { getUser } from "@faire/web/common/serialized-data/getUser";
import { getUserMilestones } from "@faire/web/common/serialized-data/getUserMilestones";
import { useQueryClient } from "@tanstack/react-query";
import { differenceInDays } from "date-fns/differenceInDays";
import { useCallback, useMemo } from "react";

import { getIsBrandMilestone } from "./utils";

export interface MilestoneState {
  hasOccurred: boolean;
  count: number;
  setHasOccurred: () => Promise<unknown>;
  incrementCount: () => Promise<unknown>;
  createdAt?: number;
  updatedAt?: number;
}

const getInitialMilestoneData = (): IGetUserMilestonesResponse | undefined => {
  const milestones = getUserMilestones();
  if (!milestones) {
    return undefined;
  }
  return IGetUserMilestonesResponse.build({
    milestones: milestones.reduce(
      (acc, milestone) => ({
        ...acc,
        [milestone.brand_milestone ?? milestone.retailer_milestone ?? ""]:
          milestone,
      }),
      {} as Record<string, IUserMilestone>
    ),
  });
};

export const useMilestonesData = ():
  | Partial<Record<string, IUserMilestone>>
  | undefined => {
  const userToken = getUser()?.token;
  const { data } = useMilestonesQuery(userToken ?? "", {
    initialData: getInitialMilestoneData(),
    enabled: !!userToken,
    staleTime: Infinity,
  });

  return data?.milestones;
};

/**
 * Base hook implementation for milestone tracking
 */
export function useMilestone<
  T extends IUserMilestone.BrandMilestone | IUserMilestone.RetailerMilestone,
>(
  isBrandMilestone: T extends IUserMilestone.BrandMilestone ? true : false,
  milestoneType: T
): MilestoneState {
  const milestones = useMilestonesData();
  const count = milestones?.[milestoneType]?.count || 0;
  const hasOccurred = count > 0;

  const record = useRecordMilestone<T>(isBrandMilestone, milestoneType);

  const setHasOccurred = useCallback(async () => {
    if (!hasOccurred) {
      return record();
    }
    return Promise.resolve();
  }, [hasOccurred, record]);

  const incrementCount = useCallback(() => record(), [record]);

  const createdAt = milestones?.[milestoneType]?.created_at;
  const updatedAt = milestones?.[milestoneType]?.updated_at;

  return useMemo(
    () => ({
      hasOccurred,
      count,
      setHasOccurred,
      incrementCount,
      createdAt,
      updatedAt,
    }),
    [count, hasOccurred, incrementCount, setHasOccurred, createdAt, updatedAt]
  );
}

/**
 * Hook to return a function that can be used to record a milestone occurrence.
 */
function useRecordMilestone<
  T extends IUserMilestone.BrandMilestone | IUserMilestone.RetailerMilestone,
>(
  isBrandMilestone: T extends IUserMilestone.BrandMilestone ? true : false,
  milestoneType: T
): () => Promise<unknown> {
  const userToken = getUser()?.token;
  const { mutateAsync } = useMilestoneMutation();
  const queryClient = useQueryClient();

  return useCallback(async () => {
    if (!userToken) {
      return Promise.resolve();
    }

    // Build the milestone with the appropriate field based on the type
    const milestoneData = getIsBrandMilestone(isBrandMilestone, milestoneType)
      ? IUserMilestone.build({
          brand_milestone: milestoneType,
        })
      : IUserMilestone.build({
          retailer_milestone: milestoneType,
        });

    const updatedMilestone = await mutateAsync([userToken, milestoneData]);

    queryClient.setQueryData<IGetUserMilestonesResponse>(
      getMilestonesQueryKey(userToken),
      (prev) => {
        return IGetUserMilestonesResponse.build({
          milestones: {
            ...prev?.milestones,
            [milestoneType]: updatedMilestone,
          },
        });
      }
    );

    return updatedMilestone;
  }, [mutateAsync, queryClient, userToken, isBrandMilestone, milestoneType]);
}

export function useRecordMilestoneOnce<
  T extends IUserMilestone.BrandMilestone | IUserMilestone.RetailerMilestone,
>(
  isBrandMilestone: T extends IUserMilestone.BrandMilestone ? true : false,
  milestoneType: T
): () => Promise<unknown> {
  const milestones = useMilestonesData();
  const record = useRecordMilestone<T>(isBrandMilestone, milestoneType);

  return useCallback(() => {
    if (!milestones?.[milestoneType]?.count) {
      return record();
    }
    return Promise.resolve();
  }, [milestones, record, milestoneType]);
}

/**
 * Hook for milestone tracking by date
 * Returns undefined if the milestone has not been recorded yet
 */
export function useMilestoneByDate<
  T extends IUserMilestone.BrandMilestone | IUserMilestone.RetailerMilestone,
>(
  isBrandMilestone: T extends IUserMilestone.BrandMilestone ? true : false,
  milestoneType: T
): {
  updatedAt: number | undefined;
  setUpdatedAtByDay: () => Promise<unknown>;
} {
  const milestones = useMilestonesData();
  const updatedAt = milestones?.[milestoneType]?.updated_at;

  const recordByDay = useRecordMilestoneByDay<T>(
    isBrandMilestone,
    milestoneType
  );

  return useMemo(
    () => ({ updatedAt, setUpdatedAtByDay: recordByDay }),
    [updatedAt, recordByDay]
  );
}

/**
 * Hook to return a function that can be used to record a milestone occurrence at most once per day
 */
function useRecordMilestoneByDay<
  T extends IUserMilestone.BrandMilestone | IUserMilestone.RetailerMilestone,
>(
  isBrandMilestone: T extends IUserMilestone.BrandMilestone ? true : false,
  milestoneType: T
): () => Promise<unknown> {
  const milestones = useMilestonesData();
  const record = useRecordMilestone<T>(isBrandMilestone, milestoneType);

  return useCallback(() => {
    const milestone = milestones?.[milestoneType];
    if (!milestone?.count) {
      return record();
    }
    const updatedAt = milestone?.updated_at ?? 0;
    const now = Date.now();
    if (differenceInDays(now, updatedAt) >= 1) {
      return record();
    }
    return Promise.resolve();
  }, [milestones, record, milestoneType]);
}
